import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { X, Send } from 'lucide-react';
import { cn } from '@/lib/utils';
import { createPageUrl } from '@/utils';
import { assistantChat } from '@/functions/assistantChat';

// ─── Constants ────────────────────────────────────────────────────────────────

// Stable message id counter — monotonically increments so recycled nodes never
// share the same key during React reconciliation.
let _msgIdSeq = 0;
const nextMsgId = () => `msg-${++_msgIdSeq}`;

const WELCOME_MESSAGE = {
  id: 'welcome',
  role: 'assistant',
  isWelcome: true, // display-only greeting — excluded from the backend payload
  content:
    "Hi! I'm your NimbleNode guide. Ask me how to create images or videos, manage credits, find your assets, or anything else in the app.",
};

const STARTER_QUICK_REPLIES = [
  'How do I create an image?',
  'How do credits work?',
  'How do I sell on the marketplace?',
  'How do I invite my team?',
];

// ─── TypingIndicator subcomponent ─────────────────────────────────────────────

function TypingIndicator() {
  return (
    <div className="flex items-start gap-2 max-w-[80%]" aria-live="polite" aria-label="Assistant is typing">
      <div className="bg-[#334155] rounded-2xl rounded-bl-sm px-4 py-3 flex items-center gap-1.5">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="w-1.5 h-1.5 rounded-full bg-[#94A3B8] animate-bounce"
            style={{ animationDelay: `${i * 150}ms`, animationDuration: '900ms' }}
          />
        ))}
      </div>
    </div>
  );
}

// ─── MessageBubble subcomponent ───────────────────────────────────────────────

function MessageBubble({ message }) {
  const isUser = message.role === 'user';

  if (message.isError) {
    return (
      <div className="flex justify-start">
        <div className="bg-[#EF4444]/20 border border-[#EF4444]/40 text-[#FCA5A5] rounded-2xl rounded-bl-sm px-4 py-3 text-sm max-w-[85%]">
          {message.content}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'px-4 py-3 text-sm leading-relaxed max-w-[80%] whitespace-pre-wrap break-words',
          isUser
            ? 'bg-[#06B6D4] text-white rounded-2xl rounded-br-sm'
            : 'bg-[#334155] text-white rounded-2xl rounded-bl-sm'
        )}
      >
        {message.content}
      </div>
    </div>
  );
}

// ─── QuickReplyChips subcomponent ─────────────────────────────────────────────

function QuickReplyChips({ replies, onSelect, disabled }) {
  if (!replies || replies.length === 0) return null;

  return (
    <div
      className="flex gap-2 overflow-x-auto pb-1 pt-1 px-4 scrollbar-hide"
      role="group"
      aria-label="Suggested follow-up questions"
    >
      {replies.map((reply) => (
        <button
          key={reply}
          type="button"
          onClick={() => !disabled && onSelect(reply)}
          disabled={disabled}
          className={cn(
            'flex-shrink-0 text-xs px-3 py-1.5 rounded-full border transition-all duration-150',
            'bg-[#334155]/60 border-[#334155] text-[#94A3B8]',
            'hover:bg-[#06B6D4]/20 hover:border-[#06B6D4]/50 hover:text-white',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#06B6D4] focus-visible:ring-offset-1 focus-visible:ring-offset-[#1E293B]',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'whitespace-nowrap'
          )}
        >
          {reply}
        </button>
      ))}
    </div>
  );
}

// ─── AssistantPanel main component ────────────────────────────────────────────

/**
 * AssistantPanel
 * Fixed-position chat panel implementing the NimbleNode Guide Assistant.
 *
 * Props:
 *   isOpen          : boolean                — controlled open state
 *   onClose         : () => void             — close handler
 *   currentPageName : string                 — passed from Layout (e.g. "Generate")
 *   launcherRef     : React.RefObject|null   — ref to the FAB; focused on close (WCAG 2.4.3)
 */
export default function AssistantPanel({ isOpen, onClose, currentPageName, launcherRef }) {
  const navigate = useNavigate();

  // ── State ──────────────────────────────────────────────────────────────────
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [quickReplies, setQuickReplies] = useState(STARTER_QUICK_REPLIES);
  const [hasUserSentMessage, setHasUserSentMessage] = useState(false);

  // ── Refs ───────────────────────────────────────────────────────────────────
  const messageListRef = useRef(null);
  const inputRef = useRef(null);
  const panelRef = useRef(null);
  const prevIsOpenRef = useRef(isOpen);

  // ── Auto-scroll to bottom on new messages ─────────────────────────────────
  useEffect(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  // ── inert attribute — keeps closed panel out of the keyboard / AT tab order ─
  // pointer-events-none only blocks mouse; `inert` covers keyboard + screen readers.
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    if (isOpen) {
      panel.removeAttribute('inert');
    } else {
      panel.setAttribute('inert', '');
    }
  }, [isOpen]);

  // ── Return focus to launcher FAB on close (WCAG 2.4.3) ────────────────────
  useEffect(() => {
    if (prevIsOpenRef.current && !isOpen && launcherRef?.current) {
      launcherRef.current.focus();
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, launcherRef]);

  // ── Focus trap + ESC close ─────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;

    // Focus the input when panel opens
    const focusTimer = setTimeout(() => {
      inputRef.current?.focus();
    }, 210); // after transition

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }

      // Focus trap — keep Tab/Shift+Tab within the panel
      if (e.key === 'Tab' && panelRef.current) {
        const focusable = panelRef.current.querySelectorAll(
          'button:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        const focusableArray = Array.from(focusable);
        if (focusableArray.length === 0) return;

        const first = focusableArray[0];
        const last = focusableArray[focusableArray.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      clearTimeout(focusTimer);
    };
  }, [isOpen, onClose]);

  // ── Send message ───────────────────────────────────────────────────────────
  const sendMessage = useCallback(
    async (text) => {
      const trimmed = (text ?? inputValue).trim();
      if (!trimmed || isLoading) return;

      // Build updated history before API call
      const userMsg = { id: nextMsgId(), role: 'user', content: trimmed };
      const nextMessages = [...messages, userMsg];

      setMessages(nextMessages);
      setInputValue('');
      setIsLoading(true);
      setQuickReplies([]); // clear chips while loading
      if (!hasUserSentMessage) setHasUserSentMessage(true);

      try {
        // Send only genuine conversation turns — exclude the display-only welcome
        // greeting and any error bubbles so they don't pollute the model's context.
        const payloadMessages = nextMessages.filter(
          (m) => !m.isWelcome && !m.isError
        );
        const { data, error } = await assistantChat({
          messages: payloadMessages,
          currentPage: currentPageName || '',
        });

        if (error || !data) {
          const errText =
            error?.message ?? 'Something went wrong. Please try again.';
          setMessages((prev) => [
            ...prev,
            { id: nextMsgId(), role: 'assistant', content: errText, isError: true },
          ]);
          setIsLoading(false);
          return;
        }

        // Append assistant reply
        setMessages((prev) => [
          ...prev,
          { id: nextMsgId(), role: 'assistant', content: data.reply },
        ]);

        // Update quick reply chips
        setQuickReplies(
          Array.isArray(data.quickReplies) ? data.quickReplies.slice(0, 4) : []
        );

        // Navigate if backend instructs it — do NOT close the panel
        if (data.navigateTo) {
          const path = createPageUrl(data.navigateTo);
          navigate(path);
          toast.info(`Taking you to ${data.navigateTo}…`, {
            duration: 2500,
            position: 'bottom-right',
          });
        }
      } catch (err) {
        setMessages((prev) => [
          ...prev,
          {
            id: nextMsgId(),
            role: 'assistant',
            content: 'Something went wrong. Please try again.',
            isError: true,
          },
        ]);
      } finally {
        setIsLoading(false);
      }
    },
    [messages, inputValue, isLoading, currentPageName, navigate, hasUserSentMessage]
  );

  // ── Input key handler — Enter sends, Shift+Enter newline ──────────────────
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div
      id="nimblenode-assistant-panel"
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-label="NimbleNode Guide Assistant"
      aria-hidden={!isOpen}
      className={cn(
        // Positioning — fixed, bottom-right, above sidebar (z-50) and Toaster (z-[100])
        'fixed z-[9999]',
        // Desktop: bottom-right with 24px margins
        'lg:bottom-6 lg:right-6 lg:w-[360px] lg:h-[520px]',
        // Mobile: wider, taller, clear mobile nav bar
        'bottom-20 right-3',
        // Mobile width: fill available space minus margin, cap at 360px
        'w-[calc(100vw-24px)] max-w-[360px]',
        // Mobile height
        'h-[70vh] max-h-[520px]',
        // Glass morphism background
        'bg-[#1E293B]/95 backdrop-blur-xl border border-[#334155] rounded-2xl shadow-2xl',
        // Flex column layout
        'flex flex-col overflow-hidden',
        // Mount animation — translate + opacity
        'transition-all duration-200 ease-out',
        isOpen
          ? 'opacity-100 translate-y-0 pointer-events-auto'
          : 'opacity-0 translate-y-2 pointer-events-none'
      )}
    >
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#334155] flex-shrink-0">
        <div className="flex items-center gap-2.5">
          {/* Cyan dot indicator */}
          <span
            className="w-2 h-2 rounded-full bg-[#06B6D4]"
            aria-hidden="true"
          />
          <span className="text-sm font-semibold text-white">
            NimbleNode Guide
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close NimbleNode Guide"
          className={cn(
            'w-8 h-8 flex items-center justify-center rounded-lg',
            'text-[#94A3B8] hover:text-white hover:bg-[#334155]/70',
            'transition-colors duration-150',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#06B6D4] focus-visible:ring-offset-1 focus-visible:ring-offset-[#1E293B]'
          )}
        >
          <X className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>

      {/* ── Message list ───────────────────────────────────────────────────── */}
      <div
        ref={messageListRef}
        className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-3"
        aria-live="polite"
        aria-label="Conversation"
        aria-relevant="additions"
      >
        {messages.map((msg) => (
          <MessageBubble key={msg.id} message={msg} />
        ))}
        {isLoading && <TypingIndicator />}
      </div>

      {/* ── Quick reply chips ──────────────────────────────────────────────── */}
      {quickReplies.length > 0 && (
        <QuickReplyChips
          replies={quickReplies}
          onSelect={sendMessage}
          disabled={isLoading}
        />
      )}

      {/* ── Input row ──────────────────────────────────────────────────────── */}
      <div className="flex items-end gap-2 px-4 py-3 border-t border-[#334155] flex-shrink-0">
        <textarea
          ref={inputRef}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
          aria-label="Ask NimbleNode Guide"
          placeholder="Ask me anything about NimbleNode…"
          rows={1}
          className={cn(
            'flex-1 resize-none bg-[#0F172A] border border-[#334155] rounded-xl',
            'px-3 py-2 text-sm text-white placeholder-[#64748B]',
            'min-h-[40px] max-h-[96px] overflow-y-auto',
            'focus:outline-none focus:ring-2 focus:ring-[#06B6D4] focus:border-transparent',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'scrollbar-thin scrollbar-thumb-[#334155] scrollbar-track-transparent',
            'transition-colors duration-150',
            // Auto-grow via CSS (3-row cap via max-h)
            'field-sizing-content'
          )}
          style={{
            // Fallback auto-grow for browsers without field-sizing support
            overflow: 'hidden',
          }}
          onInput={(e) => {
            // Auto-resize textarea height
            e.target.style.height = 'auto';
            e.target.style.height = `${Math.min(e.target.scrollHeight, 96)}px`;
          }}
        />
        <button
          type="button"
          onClick={() => sendMessage()}
          disabled={isLoading || !inputValue.trim()}
          aria-label="Send message"
          className={cn(
            'flex-shrink-0 w-10 h-10 rounded-xl',
            'flex items-center justify-center',
            'bg-[#06B6D4] hover:bg-[#0891B2]',
            'text-white transition-colors duration-150',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#06B6D4] focus-visible:ring-offset-1 focus-visible:ring-offset-[#1E293B]',
            'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#06B6D4]',
            'active:scale-95'
          )}
        >
          <Send className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
